import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { RootState } from 'store';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Collapse, Divider, Grid, LinearProgress, Stack, Typography } from '@mui/material';

import CustomButton from 'components/Button';
import SnackBar from 'components/SnackBar';
import { projectModalActions, SCOPE_MODAL } from 'features/Project/projectModalSlice';
import { useGetProjectScopeByIdQuery } from 'services/busmanApi';
import { ProjectDetailProps } from 'types/ProjectTypes';
import { PillTone } from 'types/ProjectTypes';
import { toLocaleString } from 'utils/string';

import EditScopeModal from '../component/EditScopeModal';
import Pill from '../component/Pill';
interface ProjectScopeSectionProps {
  projectDetails: ProjectDetailProps;
}

const scopeColor = {
  green: '#A3D139',
  green1: '#94C123',
  amber: '#D98B3A',
  grey: '#8B918E',
  grey1: '#969c9a',
  red: '#F44336',
};

const DataLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.09em',
      textTransform: 'uppercase',
      color: scopeColor.grey,
    }}>
    {children}
  </Typography>
);

const Delta = ({ children, isPositive = null }: { children: React.ReactNode; isPositive: boolean | null }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.75}
    sx={{ color: isPositive == null ? scopeColor.grey : isPositive ? scopeColor.green : scopeColor.amber }}>
    {isPositive == null ? <RemoveIcon /> : isPositive ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{children}</Typography>
  </Stack>
);

// const Stat = ({ label, children }: { label: string; children: React.ReactNode }) => (
//   <Stack spacing={1.25} sx={{ flex: 1 }}>
//     <DataLabel>{label}</DataLabel>
//     {children}
//   </Stack>
// );

const TimelineDot = ({ tone }: { tone: PillTone }) => {
  const map: Record<PillTone, { color: string; filled: boolean }> = {
    PENDING_APPROVAL: { color: scopeColor.amber, filled: false },
    DRAFT: { color: scopeColor.amber, filled: false },
    APPROVED: { color: scopeColor.green, filled: true },
    SUPERSEDED: { color: '#6f7572', filled: false },
    REJECTED: { color: scopeColor.red, filled: true },
  };
  const { color, filled } = map[tone];

  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: 2,
        borderColor: color,
        bgcolor: filled ? color : '#FFF',
        flexShrink: 0,
        zIndex: 1,
      }}
    />
  );
};

const VersionItem = ({
  version,
  dotTone,
  children,
}: {
  version: string;
  dotTone: PillTone;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      gap: 2,
      px: 1.5,
      py: 2,
    }}>
    <Box sx={{ pt: 0.25 }}>
      <TimelineDot tone={dotTone} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#000' }}>{version}</Typography>
        {children}
      </Stack>
    </Box>
  </Box>
);

interface ProgressSnapshot {
  id: string;
  recorded_on: string;
  percent_complete: number;
  actual_cost: number;
  actual_hours: number;
  recorded_by: string;
  recorded_at: string;
  note?: string;
}

// TODO: replace with progress snapshots from the project-scopes API once the
// endpoint is available. Seeded to match the design against the active scope.
const SAMPLE_SNAPSHOTS: ProgressSnapshot[] = [
  {
    id: '1',
    recorded_on: '2026-01-20',
    percent_complete: 62,
    actual_cost: 174300,
    actual_hours: 902,
    recorded_by: 'Priya Nair',
    recorded_at: '2026-01-20T16:05:00',
    note: 'Rig 1 fabrication complete and control cabinet wiring underway. Tracking slightly ahead on hours; hydraulic commissioning scheduled for early February.',
  },
  {
    id: '2',
    recorded_on: '2025-12-15',
    percent_complete: 41,
    actual_cost: 112400,
    actual_hours: 588,
    recorded_by: 'Priya Nair',
    recorded_at: '2025-12-15T10:30:00',
    note: 'Structural frame welded and inspected to AS 1554. Hydraulic components on order — supplier lead time flagged as a schedule risk.',
  },
  {
    id: '3',
    recorded_on: '2025-11-20',
    percent_complete: 22,
    actual_cost: 61200,
    actual_hours: 312,
    recorded_by: 'Priya Nair',
    recorded_at: '2025-11-20T09:15:00',
    note: 'Detailed design signed off by the client. Long-lead procurement released.',
  },
  {
    id: '4',
    recorded_on: '2025-10-02',
    percent_complete: 8,
    actual_cost: 21800,
    actual_hours: 118,
    recorded_by: 'Priya Nair',
    recorded_at: '2025-10-02T14:40:00',
    note: 'Concept design and HAZOP workshop complete. Baseline programme issued.',
  },
];

const SnapshotDetailField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{ borderBottom: 1, borderColor: scopeColor.grey1, pb: 1 }}>
    <Typography sx={{ fontSize: 15, color: scopeColor.grey }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
  </Stack>
);

const SnapshotRow = ({
  snapshot,
  expanded,
  onToggle,
}: {
  snapshot: ProgressSnapshot;
  expanded: boolean;
  onToggle: () => void;
}) => (
  <Box>
    <Box
      onClick={onToggle}
      sx={{
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#000', minWidth: 110 }}>
        {dayjs(snapshot.recorded_on).format('DD MMM YYYY')}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={snapshot.percent_complete}
        sx={{
          flex: 1,
          height: 10,
          borderRadius: 5,
          bgcolor: '#E3E5E0',
          '& .MuiLinearProgress-bar': { bgcolor: scopeColor.green, borderRadius: 5 },
        }}
      />
      <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 48, textAlign: 'right' }}>
        {snapshot.percent_complete}%
      </Typography>
      <ArrowDropDownIcon
        sx={{
          color: '#484848',
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        }}
      />
    </Box>

    <Collapse in={expanded} unmountOnExit>
      <Box sx={{ px: 3, pb: 3 }}>
        <Grid container spacing={{ xs: 2, md: 6 }} rowSpacing={2}>
          <Grid item xs={12} md={6}>
            <SnapshotDetailField label="Actual cost" value={toLocaleString(snapshot.actual_cost, 'currency', 0)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SnapshotDetailField label="Actual hours" value={`${snapshot.actual_hours} hrs`} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SnapshotDetailField label="Recorded by" value={snapshot.recorded_by} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SnapshotDetailField
              label="Recorded at"
              value={dayjs(snapshot.recorded_at).format('DD MMM YYYY, hh:mm a')}
            />
          </Grid>
        </Grid>
        {snapshot.note && (
          <Typography sx={{ fontSize: 18, lineHeight: 1.6, color: '#000', mt: 3 }}>{snapshot.note}</Typography>
        )}
      </Box>
    </Collapse>
  </Box>
);

/**
 *  Project Scope Section
 */
const ProjectScopeSection = ({ projectDetails }: ProjectScopeSectionProps) => {
  const {
    name,
    current_estimated_hours: durationUtilized,
    client_contact: clientDetails,
    estimated_hours: estimatedHours,
    current_estimated_cost: currentEstimatedCost,
  } = projectDetails;
  const [searchParams] = useSearchParams();
  const scopeParam = searchParams.get('scope');
  const [currentScopeId, setCurrentScopeId] = React.useState(projectDetails.current_scope?.id ?? scopeParam);
  const [message, setMessage] = React.useState({ type: '', msg: '' });
  const [expandedSnapshotId, setExpandedSnapshotId] = React.useState<string | null>(SAMPLE_SNAPSHOTS[0]?.id ?? null);
  const dispatch = useDispatch();
  const { open, mode, modalProps } = useSelector((state: RootState) => state.projectModalReducer);
  const { projectScopeList } = useSelector((state: RootState) => state.projectFilterReducer);

  const { data: projectScopeDetails } = useGetProjectScopeByIdQuery(currentScopeId, { skip: !currentScopeId });

  React.useEffect(() => {
    setCurrentScopeId(projectDetails.current_scope?.id ?? scopeParam);
  }, [scopeParam, projectDetails.current_scope?.id]);

  const estimatedHoursNum = Number(estimatedHours);
  const durationUtilizedNum = Number(durationUtilized);
  const progress =
    estimatedHoursNum > 0
      ? Math.min(
          100,
          Math.round(((Number.isFinite(durationUtilizedNum) ? durationUtilizedNum : 0) / estimatedHoursNum) * 100),
        )
      : 0;

  const scopeVersion = projectScopeDetails?.version ?? '0.0';

  const effectiveStatus =
    !projectScopeDetails?.effective_from || !projectScopeDetails?.effective_till
      ? 'Not stated'
      : new Date(projectScopeDetails?.effective_till) > new Date()
      ? 'on going'
      : 'gone over';

  const editScopeHandler = () => {
    if (!projectScopeDetails) return;
    const { project, status, ...rest } = projectScopeDetails;
    const scopeWithoutProject = { ...rest, project: project?.id, status, project_client: project.client_contact.id };
    dispatch(projectModalActions.openEditModal({ type: SCOPE_MODAL, data: scopeWithoutProject }));
  };
  return (
    <Grid container spacing={2} justifyContent="center">
      <Grid item xs={11}>
        {/* Header */}
        <Box
          sx={{
            pb: 1,
            mb: 2,
            display: 'flex',
            borderBottom: 1,
            flexWrap: 'wrap',
            borderColor: '#484848',
            gap: { xs: 2, md: 1 },
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
          }}>
          <Stack>
            <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }}>{name}</Typography>
            <Stack
              direction="row"
              sx={{
                mt: 1,
                gap: { xs: 4, md: 8 },
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
              }}>
              <Stack>
                <Typography sx={{ fontSize: 11, color: scopeColor.grey1 }}>CLIENT</Typography>
                <Typography sx={{ fontSize: 14 }}>{clientDetails.full_name}</Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontSize: 11, color: scopeColor.grey1 }}>POSITION</Typography>
                <Typography sx={{ fontSize: 14 }}>{clientDetails.position}</Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            sx={{
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'stretch',
            }}>
            <Stack
              sx={{
                minWidth: 0,
                justifyContent: 'center',
              }}>
              <Typography sx={{ fontSize: 11, letterSpacing: 1, opacity: 0.7 }}>CURRENT CONTRACT</Typography>
              <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, lineHeight: 1.2 }}>
                {toLocaleString(currentEstimatedCost, 'currency', 0)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: scopeColor.green1 }}>Active scope v{scopeVersion}</Typography>
            </Stack>

            <Stack
              sx={{
                flex: 1,
                minWidth: 160,
                justifyContent: 'center',
              }}>
              <Typography sx={{ fontSize: 11, letterSpacing: 1, opacity: 0.7 }}>PROGRESS</Typography>
              <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, lineHeight: 1.2 }}>
                {progress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  mt: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: scopeColor.green1,
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>

        {/* Box Header */}
        <Box
          sx={{
            gap: 3,
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
          }}>
          <Stack direction="column" sx={{ flex: 1 }}>
            <Box
              sx={{
                p: 3,
                flex: 1,
                border: 1,
                borderRadius: 2,
                backgroundColor: '#F4F5F2',
                borderColor: scopeColor.grey1,
              }}>
              {/* Heading */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack spacing={1}>
                  <DataLabel>Scope Version</DataLabel>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography sx={{ fontSize: 44, fontWeight: 700, lineHeight: 1 }}>v{scopeVersion}</Typography>
                    <Pill
                      label={projectScopeDetails?.status as PillTone}
                      tone={projectScopeDetails?.status as PillTone}
                    />
                  </Stack>
                </Stack>
                <CustomButton
                  text="Edit Scope"
                  data-testid="edit-scope-btn"
                  onClick={editScopeHandler}
                  variant="outlined"
                  customSx={{
                    color: scopeColor.green1,
                    borderColor: scopeColor.green1,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: 15,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      borderColor: scopeColor.green1,
                      bgcolor: 'rgba(163,209,57,0.08)',
                    },
                  }}
                />
              </Stack>

              <Divider sx={{ borderColor: scopeColor.grey, my: 2 }} />

              {/* Contract | Total Hours | Progress */}
              <Stack direction="row" spacing={4}>
                <Stack spacing={1.25} sx={{ flex: 1 }}>
                  <DataLabel>Contract value</DataLabel>
                  <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1 }}>
                    {projectDetails.current_estimated_cost}
                  </Typography>
                  <Delta isPositive={null}>{projectScopeDetails?.cost_delta}</Delta>
                </Stack>

                <Stack spacing={1.25} sx={{ flex: 1 }}>
                  <DataLabel>Total Hours</DataLabel>
                  <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1 }}>
                    {projectDetails?.current_estimated_hours}
                  </Typography>
                  <Delta isPositive={null}>{projectScopeDetails?.hours_delta}</Delta>
                </Stack>

                <Stack spacing={1.25} sx={{ flex: 1 }}>
                  <DataLabel>Progress</DataLabel>
                  <Typography sx={{ fontSize: 38, fontWeight: 700, lineHeight: 1 }}>{progress}%</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(255,255,255,0.08)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: scopeColor.green1,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <Divider sx={{ borderColor: scopeColor.grey1, my: 2 }} />

              {/* Summary Change */}
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase' }}>
                  Change Summary
                </Typography>
                <Typography sx={{ fontSize: 20, lineHeight: 1.5 }}>
                  {projectScopeDetails?.change_summary || '-'}
                </Typography>
              </Stack>

              <Box sx={{ height: 24 }} />

              {/* Effective from / till */}
              <Stack direction="row" spacing={6}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ flex: 1 }}
                  borderColor="#8b918e"
                  borderBottom={1}>
                  <Typography sx={{ color: '#8b918e' }}>Effective from</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {projectScopeDetails?.effective_from ?? 'Not stated'}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ flex: 1 }}
                  borderColor="#8b918e"
                  borderBottom={1}>
                  <Typography sx={{ color: '#8b918e' }}>Effective till</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{effectiveStatus}</Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={6}>
                <Stack direction="row" justifyContent="space-between" sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#8b918e' }}>Client approved</Typography>
                  {projectScopeDetails?.is_client_approved ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: scopeColor.green1 }}>
                      <>
                        <CheckIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 700 }}>Yes</Typography>
                      </>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: scopeColor.red }}>
                      <>
                        <ClearIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 700 }}>No</Typography>
                      </>
                    </Stack>
                  )}
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#8b918e' }}>Approved by</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {clientDetails.full_name}
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      ·{'  '}
                      {projectScopeDetails?.approved_at
                        ? dayjs(projectScopeDetails.approved_at).format('D MMM YYYY, h:mm a')
                        : 'Not stated'}
                    </Box>
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Version Scope Snapshot */}
            <Box
              sx={{
                mt: 3,
                border: 1,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#FFF',
                borderColor: scopeColor.grey1,
              }}>
              {/* Header (pinned) */}
              <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                <DataLabel>Progress Snapshots</DataLabel>
                <Typography sx={{ fontSize: 15, color: scopeColor.grey, mt: 0.5 }}>
                  {SAMPLE_SNAPSHOTS.length} recorded · captured against the active scope
                </Typography>
              </Box>

              <Divider sx={{ borderColor: scopeColor.grey1 }} />

              {/* Snapshot rows (scrollable) */}
              <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {SAMPLE_SNAPSHOTS.map((snapshot, index) => (
                  <React.Fragment key={snapshot.id}>
                    {index > 0 && <Divider sx={{ borderColor: scopeColor.grey1 }} />}
                    <SnapshotRow
                      snapshot={snapshot}
                      expanded={expandedSnapshotId === snapshot.id}
                      onToggle={() => setExpandedSnapshotId((prev) => (prev === snapshot.id ? null : snapshot.id))}
                    />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Stack>

          {/* Version History */}
          <Stack justifyContent={'space-between'} sx={{ gap: 2 }}>
            <Box
              sx={{
                border: 1,
                flexShrink: 0,
                borderRadius: 1,
                backgroundColor: '#F4F5F2',
                width: { xs: '100%', md: 360 },
              }}>
              <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
                <DataLabel>Version History</DataLabel>
              </Box>

              <Divider sx={{ borderColor: scopeColor.grey1 }} />

              {/* Timeline */}
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    top: 28,
                    width: 2,
                    left: 23,
                    bottom: 28,
                    position: 'absolute',
                    bgcolor: scopeColor.grey1,
                  }}
                />

                {projectScopeList &&
                  projectScopeList.map((details, index) => {
                    const active = details.id === currentScopeId;
                    return (
                      <Box
                        key={index}
                        onClick={() => setCurrentScopeId(details.id ?? null)}
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            bgcolor: 'action.hover',
                          },
                          borderLeft: active ? `3px solid ${scopeColor.green}` : '3px solid transparent',
                          bgcolor: active ? 'rgba(120,150,40,0.16)' : 'transparent',
                        }}>
                        <VersionItem version={`v${details.version}`} dotTone={details.status as PillTone}>
                          <Box sx={{ flex: 1 }} />
                        </VersionItem>
                        <Box sx={{ pl: 6, pr: 2, pb: 2, mt: -2 }}>
                          <Stack spacing={1}>
                            <Pill label={details.status} tone={details.status as PillTone} fullWidth />
                            <Typography sx={{ fontSize: 14 }}>Revises v{details.version}</Typography>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
            <CustomButton
              text="Add Scope"
              variant="contained"
              width="medium"
              onClick={() => dispatch(projectModalActions.openAddModal({ type: SCOPE_MODAL }))}
            />
          </Stack>
        </Box>
      </Grid>

      {open && modalProps?.type === SCOPE_MODAL && (
        <EditScopeModal
          open={open}
          isEditing={mode === 'edit'}
          setSnackBarMessage={setMessage}
          onClose={() => dispatch(projectModalActions.closeModal())}
          initialValues={modalProps?.type === SCOPE_MODAL ? modalProps.data : null}
        />
      )}

      <SnackBar
        message={message}
        onClose={() => setMessage({ type: '', msg: '' })}
        snackbarOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
};

export default ProjectScopeSection;
